package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.capacity.RemindAvailabilityUseCase
import com.evoapps.evoshift.domain.usecase.roster.GetRosterSummaryUseCase
import com.evoapps.evoshift.domain.usecase.roster.ObserveUnpublishedChangesUseCase
import com.evoapps.evoshift.domain.usecase.roster.UnpublishedChanges
import com.evoapps.evoshift.domain.usecase.todo.GetRosterProgressUseCase
import com.evoapps.evoshift.domain.usecase.todo.ObserveMyTodoUseCase
import com.evoapps.evoshift.domain.usecase.todo.ObserveTeamTodoUseCase
import com.evoapps.evoshift.domain.usecase.todo.RosterProgress
import com.evoapps.evoshift.domain.usecase.todo.TodoItem
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.capacity_reminders_sent
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapLatest
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/** SH-170: the home screen's "Do zrobienia". [loaded]: the first list arrived (no empty state before). */
data class TodoUiState(
    val loaded: Boolean = false,
    val items: List<TodoItem> = emptyList(),
    val capabilities: Set<Capability> = emptySet(),
    /** Availability requests whose reminder is on its way or was sent here (by roster id). */
    val reminded: Set<String> = emptySet(),
    val message: UiText? = null
) {
    /** Reminders can be sent (a manager while the plan runs). */
    val canRemind: Boolean get() = capabilities.shows(Gate.AVAILABILITY_CAMPAIGN)
}

/**
 * SH-170: "Do zrobienia" at the current workplace: the team's (owners and managers: who may build
 * rosters or decide requests) or the person's own, live, most urgent first. Its one inline action
 * is the availability reminder; the rest open their screens. [refresh] reads it again (back on the
 * home screen after typing takings, say).
 */
@OptIn(ExperimentalCoroutinesApi::class)
class TodoViewModel(
    getCurrentUserUseCase: GetCurrentUserUseCase,
    sessionManager: WorkplaceSessionManager,
    private val observeTeamTodo: ObserveTeamTodoUseCase,
    private val observeMyTodo: ObserveMyTodoUseCase,
    private val remindAvailability: RemindAvailabilityUseCase? = null,
    plans: ObservePlanActiveUseCase? = null
) : ViewModel() {
    private val _uiState = MutableStateFlow(TodoUiState())
    val uiState = _uiState.asStateFlow()
    private val attempts = MutableStateFlow(0)

    init {
        viewModelScope.launch {
            val user = getCurrentUserUseCase()
            sessionManager.currentWorkplaceId.flatMapLatest { workplaceId ->
                if (workplaceId == null) flowOf(Triple<String?, String?, Set<Capability>>(null, null, emptySet()))
                else combine(user.map { it?.uid }.distinctUntilChanged(), capabilitiesAt(user, plans, workplaceId)) { uid, caps ->
                    Triple(uid, workplaceId, caps)
                }
            }.distinctUntilChanged().combine(attempts) { key, _ -> key }.flatMapLatest { (uid, workplaceId, caps) ->
                _uiState.update { it.copy(capabilities = caps) }
                when {
                    uid == null || workplaceId == null -> flowOf(emptyList())
                    Capability.EDIT_ROSTER in caps || Capability.APPROVE_REQUESTS in caps -> observeTeamTodo(workplaceId, caps)
                    Capability.WORK_SHIFTS in caps -> observeMyTodo(uid, workplaceId)
                    else -> flowOf(emptyList())
                }.catch { emit(emptyList()) }
            }.collect { items -> _uiState.update { it.copy(loaded = true, items = items) } }
        }
    }

    fun refresh() {
        attempts.value++
    }

    /**
     * "Przypomnij": an availability notification to everyone of [item] who has not answered
     * (and has the app). [title] and [message] are the push text in the sender's language.
     */
    fun remind(item: TodoItem.AvailabilityAnswers, title: String, message: String) {
        val remind = remindAvailability ?: return
        val state = _uiState.value
        if (!state.canRemind || item.rosterId in state.reminded || item.remindable.isEmpty()) return
        _uiState.update { it.copy(reminded = it.reminded + item.rosterId) }
        viewModelScope.launch {
            remind(item.rosterId, item.remindable, title, message).fold(
                onSuccess = { count -> _uiState.update { it.copy(message = UiText.Resource(Res.string.capacity_reminders_sent, listOf(count))) } },
                onFailure = { e -> _uiState.update { it.copy(reminded = it.reminded - item.rosterId, message = e.toUiText()) } }
            )
        }
    }

    fun messageShown() = _uiState.update { it.copy(message = null) }
}

/** SH-170: the roster cards' trackers (by roster id) and the reminders sent from them. */
data class RosterTrackerUiState(
    val progress: Map<String, RosterProgress> = emptyMap(),
    val reminded: Set<String> = emptySet(),
    val capabilities: Set<Capability> = emptySet(),
    val message: UiText? = null,
    /** SH-263: published months (not ended) with changes not published yet, by roster id: the card's badge. */
    val unpublished: Map<String, UnpublishedChanges> = emptyMap()
)

/**
 * SH-170: where each roster of the current workplace stands (demand, availability, assignments,
 * publication) with its numbers, read again whenever the rosters change; "Przypomnij" for the
 * availability step. Only for those who build rosters.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class RosterTrackerViewModel(
    getCurrentUserUseCase: GetCurrentUserUseCase,
    sessionManager: WorkplaceSessionManager,
    private val getRosterSummary: GetRosterSummaryUseCase,
    private val getProgress: GetRosterProgressUseCase,
    private val remindAvailability: RemindAvailabilityUseCase? = null,
    plans: ObservePlanActiveUseCase? = null,
    private val observeUnpublished: ObserveUnpublishedChangesUseCase? = null
) : ViewModel() {
    private val _uiState = MutableStateFlow(RosterTrackerUiState())
    val uiState = _uiState.asStateFlow()
    private val attempts = MutableStateFlow(0)

    init {
        viewModelScope.launch {
            val user = getCurrentUserUseCase()
            sessionManager.currentWorkplaceId.flatMapLatest { workplaceId ->
                if (workplaceId == null) flowOf(null to emptySet())
                else capabilitiesAt(user, plans, workplaceId).map { workplaceId to it }
            }.distinctUntilChanged().combine(attempts) { key, _ -> key }.flatMapLatest { (workplaceId, caps) ->
                _uiState.update { it.copy(capabilities = caps) }
                if (workplaceId == null || Capability.EDIT_ROSTER !in caps) flowOf(emptyMap())
                else getRosterSummary(workplaceId).catch { emit(emptyList()) }.distinctUntilChanged().mapLatest { rosters -> progressOf(rosters) }
            }.collect { progress -> _uiState.update { it.copy(progress = progress) } }
        }
        // SH-263: the months' unpublished changes, live from their shifts (a publish clears the badge).
        if (observeUnpublished != null) viewModelScope.launch {
            val user = getCurrentUserUseCase()
            sessionManager.currentWorkplaceId.flatMapLatest { workplaceId ->
                if (workplaceId == null) flowOf(null to emptySet())
                else capabilitiesAt(user, plans, workplaceId).map { workplaceId to it }
            }.distinctUntilChanged().flatMapLatest { (workplaceId, caps) ->
                if (workplaceId == null || !caps.shows(Gate.PUBLISH_ROSTER)) flowOf(emptyMap())
                else getRosterSummary(workplaceId).catch { emit(emptyList()) }.distinctUntilChanged()
                    .flatMapLatest { rosters -> observeUnpublished(rosters) }.catch { emit(emptyMap()) }
            }.distinctUntilChanged().collect { changes -> _uiState.update { it.copy(unpublished = changes) } }
        }
    }

    private suspend fun progressOf(rosters: List<Roster>): Map<String, RosterProgress> =
        rosters.filter { it.status == RosterStatus.SKELETON || it.status == RosterStatus.DRAFT }
            .mapNotNull { roster -> getProgress(roster).getOrNull()?.let { roster.id to it } }
            .toMap()

    /** Reads the trackers again (back on the list after building a month). */
    fun refresh() {
        attempts.value++
    }

    fun remind(progress: RosterProgress, title: String, message: String) {
        val remind = remindAvailability ?: return
        val state = _uiState.value
        if (!state.capabilities.shows(Gate.AVAILABILITY_CAMPAIGN) || progress.rosterId in state.reminded || progress.remindable.isEmpty()) return
        _uiState.update { it.copy(reminded = it.reminded + progress.rosterId) }
        viewModelScope.launch {
            remind(progress.rosterId, progress.remindable, title, message).fold(
                onSuccess = { count -> _uiState.update { it.copy(message = UiText.Resource(Res.string.capacity_reminders_sent, listOf(count))) } },
                onFailure = { e -> _uiState.update { it.copy(reminded = it.reminded - progress.rosterId, message = e.toUiText()) } }
            )
        }
    }

    fun messageShown() = _uiState.update { it.copy(message = null) }
}
